/**
 * @packageDocumentation
 * @category State
 */

import { createGeocoder, hasGeocoder } from 'BomdisoftComponents/inputs/location/google-maps';
import { CountryForFilter, MapLocation, PlaceType } from 'BomdisoftComponents/types';
import { devtools } from '@bomdisoft/store/middleware';
import { create } from '@bomdisoft/store/react';
import { GeocodeData, getDeviceLocation, getGeocodeDataFromCoordinates } from '@bomdisoft/utilities';

const defaultLocation: MapLocation = {
  latitude: 59.42920646651428,
  longitude: 24.788915511951,
  name: 'Default location',
};

type LocationStore = {
  disableInputs: boolean;
  placeType: PlaceType | null;
  location: MapLocation | null;
  coordinates: string | null;
  address: string | null;
  city: string | null;
  postcode: string | null;
  county: string | null;
  country: string | null;
  country_code: string | null;
  selectedCountry?: CountryForFilter | null;
  countries: CountryForFilter[];
  setLocationPlaceType: (placeType: PlaceType | null) => void;
  setLocation: (location: MapLocation | null) => void;
  setLocationCoordinates: (coordinates: string | null) => void;
  setLocationAddress: (address: string | null) => void;
  setLocationCity: (value: string | null) => void;
  setLocationCountry: (value: string | null) => void;
  setLocationSelectedCountry: (value: CountryForFilter | null) => void;
  setLocationCounty: (value: string | null) => void;
  setLocationPostcode: (value: string | null) => void;
  setLocationCountries: (countries: CountryForFilter[]) => void;
  setDisableInputs: (disableInputs: boolean) => void;
  setLocationFields: (data: GeocodeData) => void;
  resetLocation: () => void;
};

export const useLocationStore = create<LocationStore>()(
  devtools((set, get) => ({
    address: null,

    city: null,

    coordinates: null,

    countries: [],

    country: null,

    country_code: null,

    county: null,

    disableInputs: false,

    location: defaultLocation,
    placeType: null,

    postcode: null,
    resetLocation: () => {
      set({ location: defaultLocation });
    },

    selectedCountry: null,
    setDisableInputs: async (disableInputs: boolean) => {
      set({ disableInputs });
    },
    setLocation: (location: MapLocation | null) => {
      set({ location });
    },
    setLocationAddress: async (address: string | null) => {
      set({ address });
    },
    setLocationCity: async (city: string | null) => {
      set({ city });
    },
    setLocationCoordinates: async (coordinates: string | null) => {
      if (coordinates && coordinates.split(',').length === 2) {
        const [latitude, longitude] = coordinates.trim().split(',');
        if (latitude && longitude && hasGeocoder(window)) {
          try {
            const data = await getGeocodeDataFromCoordinates({ lat: Number(latitude), lng: Number(longitude) });
            if (data) {
              const countries = get().countries;
              const updated: Partial<LocationStore> = {
                ...data,
                disableInputs: false,
                selectedCountry: countries.find(c => c.name.toLowerCase() == data.country?.toLowerCase()),
              };

              if (updated.city && updated.postcode && updated.county && updated.selectedCountry) updated.disableInputs = true;

              set(updated);
            } else set({ address: null, location: null, placeType: null });
          } catch {
            set({ address: null, location: null, placeType: null });
          }
        }
      }

      set({ coordinates });
    },
    setLocationCountries: async (countries: CountryForFilter[]) => {
      set({ countries });
    },
    setLocationCountry: async (country: string | null) => {
      set({ country });
    },
    setLocationCounty: async (county: string | null) => {
      set({ county });
    },
    setLocationFields: (data: GeocodeData) => {
      const countries = get().countries;
      const updated: Partial<LocationStore> = {
        ...data,
        disableInputs: false,
        selectedCountry: countries.find(c => c.name.toLowerCase() == data.country?.toLowerCase()),
      };

      if (updated.city && updated.postcode && updated.county && updated.selectedCountry) updated.disableInputs = true;

      set(updated);
    },
    setLocationPlaceType: (placeType: PlaceType | null) => {
      let address = get().address;
      const countries = get().countries;

      if (placeType) {
        address = placeType.description;

        const geocoder = createGeocoder(window);
        if (geocoder) {
          geocoder.geocode({ placeId: placeType.place_id }, (results, status) => {
            const selected = results?.[0];
            if (status == 'OK' && selected) {
              const latitude = selected.geometry.location.lat();
              const longitude = selected.geometry.location.lng();
              const updated: Partial<LocationStore> = {
                address: selected.formatted_address,
                coordinates: `${latitude},${longitude}`,
                disableInputs: false,
                location: { latitude, longitude, name: placeType.description },
              };

              for (let i = 0; i < selected.address_components.length; i++) {
                if (selected.address_components[i].types[0] == 'locality') updated.city = selected.address_components[i].long_name;
                if (selected.address_components[i].types[0] == 'administrative_area_level_1') updated.county = selected.address_components[i].long_name;
                if (selected.address_components[i].types[0] == 'country') {
                  updated.country = selected.address_components[i].long_name;
                  updated.selectedCountry = countries.find(c => c.name.toLowerCase() == selected.address_components[i].long_name.toLowerCase());
                }
                if (selected.address_components[i].types[0] == 'postal_code') updated.postcode = selected.address_components[i].long_name;
              }

              if (updated.city && updated.postcode && updated.county && updated.selectedCountry) updated.disableInputs = true;

              set(updated);
            } else {
              console.error(`Place geocode was not successful: ${status}`);
              getDeviceLocation({
                callback: (position: GeolocationPosition) => {
                  set({
                    coordinates: `${position.coords.latitude},${position.coords.longitude}`,
                    location: { latitude: position.coords.latitude, longitude: position.coords.longitude, name: placeType.description },
                  });
                },
              });
            }
          });
        }
      }

      set({ address, placeType });
    },
    setLocationPostcode: async (postcode: string | null) => {
      set({ postcode });
    },
    setLocationSelectedCountry: async (selectedCountry: CountryForFilter | null) => {
      set({ selectedCountry });
    },
  })),
);
